<template>
  <div>
    <VerifyIdentityError
      v-if="availableMfaModalities.length === 0"
      class="md:w-480 md:mx-auto"
      :errorTitle="$t('common.no_contact_info_found')"
      :errorBody="$t('common.contact_health_plan_csr.help', [helpNumber])"
    />
    <div v-else>
      <RegistrationLayoutTitles :title="$t('common.verify_your_identity')" />
      <div class="flex flex-col md:w-458 md:mx-auto">
        <p class="text-sm mt-5 text-grey-600 text-center mb-10">
          {{ $t('registration.mfa_preference.explanation') }}
        </p>
        <Form :validator="$v.model" :error="error">
          <FormRadioSelect
            v-model="model.mfaPreference"
            :data="availableMfaModalities"
            name="mfaPreference"
            :helperText="
              $t('common.contact_health_plan_csr.contact_info_change', [
                helpNumber,
              ])
            "
            helperTextIconClass="fas fa-info-circle"
          />
        </Form>
        <div class="flex flex-col w-full">
          <HMButton
            id="nextButton"
            rounded="full"
            fontWeight="semibold"
            class="w-full tracking-wide"
            :class="{ 'mt-10': formHasError, 'mt-25': !formHasError }"
            :busy="isFetching"
            @click="handleNext"
          >
            {{ $t('common.next_btn') }}
          </HMButton>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { Form, FormRadioSelect } from '../../../common/forms'
import VerifyIdentityError from './VerifyIdentityError.vue'
import RegistrationLayoutTitles from '@components/layouts/RegistrationLayoutTitles'
import { mapActions, mapGetters } from 'vuex'
import { MFA_TYPES } from '@platform-shared/constants'
import { formatPhoneNumber } from '@platform-shared/utils/formatters'
import supportMixin from '../../../mixins/support'
import verifyIdentityMixin from '@platform-shared/mixins/verifyIdentityMixin'
import _orderBy from 'lodash/orderBy'
import { required } from 'vuelidate/lib/validators'

export default {
  components: {
    RegistrationLayoutTitles,
    FormRadioSelect,
    Form,
    VerifyIdentityError,
  },
  mixins: [supportMixin, verifyIdentityMixin],
  props: {
    isSSORegister: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      modalityOrder: [MFA_TYPES.SMS, MFA_TYPES.VOICE, MFA_TYPES.EMAIL],
      model: {
        mfaPreference: undefined,
      },
      error: null,
    }
  },
  validations: {
    model: { mfaPreference: { required } },
  },
  computed: {
    ...mapGetters('registration', ['isFetching', 'mfaPreference']),
    ...mapGetters('member', ['memberSsoContactInfo']),
    formHasError() {
      return !!this.error
    },
    helpNumber() {
      return this.supportClientNumber || this.supportNumber
    },
    availableMfaModalities() {
      let availableModalities = []

      Object.values(MFA_TYPES).forEach((mfaType) => {
        switch (mfaType) {
          case MFA_TYPES.SMS:
            if (this.coalesceContactInfo.primarySmsPhone)
              availableModalities.push({
                code: mfaType,
                label: this.$t('mfa.modality.label.sms', [
                  formatPhoneNumber(this.coalesceContactInfo.primarySmsPhone),
                ]),
              })
            break
          case MFA_TYPES.VOICE:
            if (this.coalesceContactInfo.primaryPhone)
              availableModalities.push({
                code: mfaType,
                label: this.$t('mfa.modality.label.voice', [
                  formatPhoneNumber(this.coalesceContactInfo.primaryPhone),
                ]),
              })
            break
          case MFA_TYPES.EMAIL:
            if (this.coalesceContactInfo.email)
              availableModalities.push({
                code: mfaType,
                label: this.$t('mfa.modality.label.email', [
                  this.coalesceContactInfo.email,
                ]),
              })
            break
        }
      })
      return _orderBy(
        availableModalities,
        [(modality) => this.modalityOrder.indexOf(modality.code)],
        ['asc']
      )
    },
  },
  mounted() {
    this.model.mfaPreference = this.mfaPreference
  },
  methods: {
    ...mapActions('registration', ['submitMfaPreference']),
    ...mapActions('member', ['requestMfaCode']),
    handleSubmit(mfaPreference) {
      this.submitMfaPreference(mfaPreference)
        .then(() => {
          this.$emit('next')
        })
        .catch((error) => {
          this.error = error.json.message
        })
    },
    handleNext() {
      this.error = null
      this.$v.$touch()

      if (this.$v.$invalid) return
      const model = this.model

      this.handleSubmit(model.mfaPreference)
    },
  },
}
</script>
